<template>
  <div
    class="right-drawer"
    :class="{ 'right-drawer--open': isOpen }"
  >
    <!-- Vertical handle -->
    <button
      type="button"
      class="right-drawer__handle top-1/2 -translate-y-1/2 z-50 group"
      @click="toggleDrawer"
      :aria-expanded="isOpen"
      aria-label="Toggle quick search drawer"
    > 
     
<div
    class="
      absolute right-0 flex-col
      flex items-center 
      text-white 
      rounded-l-2xl
      shadow-xl
      hover:pr-4
      transition-all duration-300 qsearch-bar
    "
  >
     <span class="absolute inset-0 rounded-l-2xl opacity-40 animate-ping"></span>
    <!-- Search Icon -->
    <svg
      xmlns="http://www.w3.org/2000/svg"
      class="w-4 h-4 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M21 21l-4.35-4.35m1.85-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
      />
    </svg>

    <!-- Vertical Text -->
    <span
      class="
        writing-mode-vertical
        rotate-180
        text-xs
        font-semibold
        tracking-wide
      "
    >
      Quick Search
    </span>

  </div>


    </button>

    <!-- Drawer content -->
    <div v-if="isOpen" class="right-drawer__panel">
      <header class="right-drawer__header">
        <h3 class="right-drawer__title">Quick search</h3>
        <button
          type="button"
          class="right-drawer__close"
          @click="closeDrawer"
          aria-label="Close quick search drawer"
        >
          ✕
        </button>
      </header>

      <TabView
        :activeIndex="activeTabIndex"
        @update:activeIndex="(idx) => (activeTabIndex = idx)"
        class="right-drawer__tabs"
      >
        <!-- Orders Tab -->
        <TabPanel header="Orders">
          <section class="right-drawer__section">
            <label class="field-label" for="orderNumber">
              Order Number
            </label>
            <div class="field-row">
              <InputText
                id="orderNumber"
                v-model="orderQuery"
                placeholder="#1001"
                class="field-input"
                @keyup.enter="searchOrders"
              />
              <Button
                label="Search"
                size="small"
                :loading="ordersLoading"
                :disabled="!orderQueryTrimmed"
                @click="searchOrders"
              />
            </div>
           

            <p v-if="ordersError" class="error-text">
              {{ ordersError }}
            </p>

            <div v-if="ordersLoading" class="loading-text">
              Loading orders...
            </div>

            <div
              v-else-if="ordersResults.length === 0 && hasSearchedOrders"
              class="empty-text"
            >
              No orders found for this number.
            </div>

            <div
              v-else-if="orderDetailsLoading && !orderDetails && hasSearchedOrders"
              class="loading-text"
            >
              Loading order details...
            </div>

            <!-- When we have a primary match, show its full details inline using an accordion -->
            <div v-else-if="orderDetails">
              <div class="order-details-header">
                <div>
                  <div class="order-details-header__number">
                    {{ orderDetails.orderNumber || orderDetails.rawData?.name || 'Order' }}
                  </div>
                  <div class="order-details-header__meta">
                    <span
                      v-if="orderDetails.financialStatus"
                      class="badge"
                      :class="statusBadgeClass(orderDetails.financialStatus, 'financial')"
                    >
                      {{ orderDetails.financialStatus }}
                    </span>
                    <span
                      v-if="orderDetails.fulfillmentStatus"
                      class="badge"
                      :class="statusBadgeClass(orderDetails.fulfillmentStatus, 'fulfillment')"
                    >
                      {{ orderDetails.fulfillmentStatus }}
                    </span>
                  </div>
                </div>
              </div>

              <p v-if="drawerLiveError" class="error-text">
                {{ drawerLiveError }}
              </p>

              <p v-if="orderDetailsError" class="error-text">
                {{ orderDetailsError }}
              </p>

              <div v-if="orderDetailsLoading" class="loading-text">
                Loading order details...
              </div>

              <Accordion v-else class="drawer-order-accordion" :multiple="true" :activeIndex="[0]">
                <AccordionTab header="Summary">
                  <div class="summary-grid">
                    <div>
                      <div class="summary-label">Order number</div>
                      <div class="summary-value">
                        {{ orderDetails.orderNumber || orderDetails.rawData?.name || '—' }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Created</div>
                      <div class="summary-value">
                        {{ formatDate(orderDetails.createdAt || orderDetails.rawData?.createdAt) }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Financial status</div>
                      <div class="summary-value">
                        {{ orderDetails.financialStatus || orderDetails.rawData?.displayFinancialStatus || '—' }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Fulfillment status</div>
                      <div class="summary-value">
                        {{ orderDetails.fulfillmentStatus || orderDetails.rawData?.displayFulfillmentStatus || '—' }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Delivery status</div>
                      <div class="summary-value">
                        {{ orderDetails.deliveryStatus || orderDetails.rawData?.deliveryStatus || '—' }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Delivery method</div>
                      <div class="summary-value">
                        {{ orderDetails.deliveryMethod || orderDetails.rawData?.shippingLine?.title || '—' }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Channel</div>
                      <div class="summary-value">
                        {{ orderDetails.channel || '—' }}
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab v-if="orderCustomer" header="Customer">
                  <div class="summary-grid">
                    <div class="summary-span">
                      <div class="summary-label">Name</div>
                      <div class="summary-value">
                        {{ orderCustomer.fullName || (orderCustomer.firstName + ' ' + orderCustomer.lastName) || '—' }}
                      </div>
                    </div>
                    <div class="summary-span">
                      <div class="summary-label">Email</div>
                      <div class="summary-value">
                        {{ orderCustomer.email || '—' }}
                      </div>
                    </div>
                    <div class="summary-span">
                      <div class="summary-label">Phone</div>
                      <div class="summary-value">
                        {{ orderCustomer.phone || '—' }}
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab v-if="orderDetailsLineItems.length" header="Items">
                  <ul class="items-list">
                    <li
                      v-for="item in orderDetailsLineItems"
                      :key="item.id || item.title"
                      class="items-list__item"
                    >
                      <div class="items-list__title">
                        {{ item.title }}
                      </div>
                      <div class="items-list__meta">
                        <span>Qty: {{ item.quantity }}</span>
                        <span v-if="item.variantTitle"> • {{ item.variantTitle }}</span>
                        <span v-if="item.sku"> • SKU: {{ item.sku }}</span>
                      </div>
                    </li>
                  </ul>
                </AccordionTab>

                <AccordionTab header="Addresses">
                  <div class="summary-grid">
                    <div class="summary-span">
                      <div class="summary-label">Shipping address</div>
                      <div class="summary-value summary-value--pre">
                        {{ formatAddress(orderDetails.rawData?.shippingAddress) }}
                      </div>
                    </div>
                    <div class="summary-span">
                      <div class="summary-label">Billing address</div>
                      <div class="summary-value summary-value--pre">
                        {{ formatAddress(orderDetails.rawData?.billingAddress) }}
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab header="Payment">
                  <div class="summary-grid">
                    <div>
                      <div class="summary-label">Total</div>
                      <div class="summary-value">
                        {{ formatMoney(orderDetails.rawData?.totalPriceSet?.shopMoney) }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Subtotal</div>
                      <div class="summary-value">
                        {{ formatMoney(orderDetails.rawData?.subtotalPriceSet?.shopMoney) }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Tax</div>
                      <div class="summary-value">
                        {{ formatMoney(orderDetails.rawData?.totalTaxSet?.shopMoney) }}
                      </div>
                    </div>
                    <div>
                      <div class="summary-label">Shipping</div>
                      <div class="summary-value">
                        {{ formatMoney(orderDetails.rawData?.totalShippingPriceSet?.shopMoney) }}
                      </div>
                    </div>
                    <div class="summary-span">
                      <div class="summary-label">Discounts</div>
                      <div class="summary-value">
                        {{ formatMoney(orderDetails.rawData?.totalDiscountsSet?.shopMoney) }}
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab v-if="canShowFulfillmentTab" header="Fulfillment & shipment">
                  <div
                    v-if="orderDetailsLoading && drawerFulfillments.length === 0"
                    class="loading-text"
                  >
                    Loading fulfillments...
                  </div>
                  <div
                    v-else-if="drawerFulfillments.length === 0"
                    class="empty-text"
                  >
                    No fulfillment records for this order yet.
                  </div>
                  <div v-else class="summary-grid">
                    <div
                      v-for="ff in drawerFulfillments"
                      :key="ff.id || ff.fulfillmentOrderId || ff.name || ff.status || ff.createdAt"
                      class="summary-span fulfillment-card"
                    >
                      <div class="fulfillment-card__header">
                        <span class="summary-label">Status</span>
                        <span
                          class="badge"
                          :class="statusBadgeClass(ff.status || ff.fulfillmentStatus, 'fulfillment')"
                        >
                          {{ ff.status || ff.fulfillmentStatus || '—' }}
                        </span>
                      </div>
                      <div class="fulfillment-card__body">
                        <div class="summary-grid">
                          <div>
                            <div class="summary-label">Created</div>
                            <div class="summary-value">
                              {{ formatDate(ff.createdAt || ff.created_at) || '—' }}
                            </div>
                          </div>
                          <div>
                            <div class="summary-label">Updated</div>
                            <div class="summary-value">
                              {{ formatDate(ff.updatedAt || ff.updated_at) || '—' }}
                            </div>
                          </div>
                          <div class="summary-span">
                            <div class="summary-label">Tracking</div>
                            <div class="summary-value">
                              <template
                                v-if="ff.tracking && (ff.tracking.url || ff.tracking.number)"
                              >
                                <a
                                  v-if="ff.tracking.url"
                                  :href="ff.tracking.url"
                                  class="link"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {{ ff.tracking.number || 'View tracking' }}
                                </a>
                                <span v-else>
                                  {{ ff.tracking.number }}
                                </span>
                                <span v-if="ff.tracking.company" class="summary-muted">
                                  • {{ ff.tracking.company }}
                                </span>
                              </template>
                              <span v-else>—</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionTab>

                <AccordionTab v-if="canShowTransactionsTab" header="Transactions">
                  <div
                    v-if="orderDetailsLoading && drawerTransactions.length === 0"
                    class="loading-text"
                  >
                    Loading transactions...
                  </div>
                  <div
                    v-else-if="drawerTransactions.length === 0"
                    class="empty-text"
                  >
                    No transaction records for this order yet.
                  </div>
                  <ul v-else class="items-list">
                    <li
                      v-for="tx in drawerTransactions"
                      :key="tx.id || tx.shopifyTransactionId || tx.gateway || tx.processedAt"
                      class="items-list__item transaction-item"
                    >
                      <div class="items-list__title transaction-item__row">
                        <span>{{ tx.kind || tx.type || 'Transaction' }}</span>
                        <span
                          class="badge"
                          :class="statusBadgeClass(tx.status, 'transaction')"
                        >
                          {{ tx.status || '—' }}
                        </span>
                      </div>
                      <div class="items-list__meta transaction-item__row">
                        <span>
                          {{ formatTransactionAmount(tx) }}
                        </span>
                        <span v-if="tx.gateway" class="summary-muted">
                          • {{ tx.gateway }}
                        </span>
                      </div>
                      <div class="items-list__meta">
                        <span class="summary-muted">
                          {{ formatDate(tx.processedAt || tx.processed_at) || '—' }}
                        </span>
                      </div>
                    </li>
                  </ul>
                </AccordionTab>

                <AccordionTab v-if="canShowTimelineTab" header="Timeline">
                  <div
                    v-if="orderDetailsLoading && drawerTimeline.length === 0"
                    class="loading-text"
                  >
                    Loading timeline...
                  </div>
                  <div
                    v-else-if="drawerTimeline.length === 0"
                    class="empty-text"
                  >
                    No timeline events for this order yet.
                  </div>
                  <ul v-else class="timeline-list">
                    <li
                      v-for="(event, idx) in drawerTimeline"
                      :key="event.id || event.shopifyEventId || event.createdAt || idx"
                      class="timeline-list__item"
                    >
                      <div class="timeline-list__meta">
                        <span class="timeline-list__date">
                          {{ formatDate(event.createdAt || event.eventAt) || '—' }}
                        </span>
                        <span v-if="event.eventType" class="badge badge--muted">
                          {{ event.eventType }}
                        </span>
                      </div>
                      <div class="timeline-list__message">
                        {{ event.message || event.description || 'Event' }}
                      </div>
                      <div v-if="event.author" class="timeline-list__author">
                        {{ event.author }}
                      </div>
                    </li>
                  </ul>
                </AccordionTab>
              </Accordion>
            </div>
          </section>
        </TabPanel>

        <!-- Customers Tab -->
        <TabPanel header="Customers">
          <section class="right-drawer__section">
            <label class="field-label" for="customerEmail">
              Email
            </label>
            <InputText
              id="customerEmail"
              v-model="customerEmail"
              placeholder="customer@example.com"
              class="field-input"
              @keyup.enter="searchCustomers"
            />

            <label class="field-label" for="customerPhone">
              Phone
            </label>
            <InputText
              id="customerPhone"
              v-model="customerPhone"
              placeholder="+1 555 555 5555"
              class="field-input"
              @keyup.enter="searchCustomers"
            />

            <div class="field-row field-row--mt">
              <Button
                label="Search"
                size="small"
                :loading="customersLoading"
                :disabled="!hasCustomerQuery"
                @click="searchCustomers"
              />
            </div>
            <p class="field-help">
              Search by email and/or phone. We’ll show matching customer and their
              orders.
            </p>

            <p v-if="customersError" class="error-text">
              {{ customersError }}
            </p>

            <div v-if="customersLoading" class="loading-text">
              Loading customer...
            </div>

            <div
              v-else-if="hasSearchedCustomers && !customer && customerOrders.length === 0 && !customersError"
              class="empty-text"
            >
              No customers found for these details.
            </div>

            <div v-else-if="customer" class="customer-summary">
              <h4 class="customer-summary__name">
                {{ customer.fullName || customer.email || 'Customer' }}
              </h4>
              <p class="customer-summary__meta">
                <span v-if="customer.email">{{ customer.email }}</span>
                <span v-if="customer.phone" class="customer-summary__sep">•</span>
                <span v-if="customer.phone">{{ customer.phone }}</span>
              </p>
              <p class="customer-summary__meta">
                <span v-if="customer.ordersCount != null">
                  Orders: {{ customer.ordersCount }}
                </span>
                <span
                  v-if="customer.totalSpent != null"
                  class="customer-summary__sep"
                >
                  •
                </span>
                <span v-if="customer.totalSpent != null">
                  Total spent: {{ formatCurrency(customer.totalSpent) }}
                </span>
              </p>
            </div>

            <div v-if="customerOrders.length > 0" class="customer-orders">
              <h5 class="customer-orders__title">Orders</h5>
              <ul class="results-list results-list--compact">
                <li
                  v-for="order in customerOrders"
                  :key="order.id"
                  class="results-list__item"
                >
                  <div class="results-list__main">
                    <button
                      type="button"
                      class="results-list__order-link"
                      @click="openOrderInDrawer(order)"
                    >
                      <span class="results-list__order-number">
                        {{ order.orderNumber || '—' }}
                      </span>
                    </button>
                    <p class="results-list__meta">
                      <span
                        v-if="order.financialStatus"
                        class="badge"
                        :class="statusBadgeClass(order.financialStatus, 'financial')"
                      >
                        {{ order.financialStatus }}
                      </span>
                      <span
                        v-if="order.fulfillmentStatus"
                        class="badge"
                        :class="statusBadgeClass(order.fulfillmentStatus, 'fulfillment')"
                      >
                        {{ order.fulfillmentStatus }}
                      </span>
                    </p>
                    <p v-if="order.createdAt" class="results-list__date">
                      Created: {{ formatDate(order.createdAt) }}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </section>
        </TabPanel>
      </TabView>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import axios from "axios";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import InputText from "primevue/inputtext";
import Button from "primevue/button";

const emit = defineEmits(["drawer-open", "drawer-close"]);

const isOpen = ref(false);
const activeTabIndex = ref(0);

// Orders tab state
const orderQuery = ref("");
const ordersLoading = ref(false);
const ordersError = ref("");
const ordersResults = ref([]);
const hasSearchedOrders = ref(false);

// Order details state (for first / primary match)
const orderDetails = ref(null);
const orderCustomer = ref(null);
const orderDetailsLoading = ref(false);
const orderDetailsError = ref("");
const drawerFulfillments = ref([]);
const drawerTransactions = ref([]);
const drawerTimeline = ref([]);
const drawerLiveError = ref("");
const orderCapabilities = ref(null);

const orderQueryTrimmed = computed(() => orderQuery.value.trim());
const canShowFulfillmentTab = computed(
  () => orderCapabilities.value?.hasFulfillments !== false
);
const canShowTransactionsTab = computed(
  () => orderCapabilities.value?.hasTransactions !== false
);
const canShowTimelineTab = computed(
  () => orderCapabilities.value?.hasTimeline !== false
);

// Customers tab state
const customerEmail = ref("");
const customerPhone = ref("");
const customersLoading = ref(false);
const customersError = ref("");
const hasSearchedCustomers = ref(false);
const customer = ref(null);
const customerOrders = ref([]);

const hasCustomerQuery = computed(
  () => customerEmail.value.trim() !== "" || customerPhone.value.trim() !== ""
);

/** Reset all drawer form/results so reopening starts clean. */
function resetDrawerState() {
  activeTabIndex.value = 0;

  orderQuery.value = "";
  ordersLoading.value = false;
  ordersError.value = "";
  ordersResults.value = [];
  hasSearchedOrders.value = false;

  orderDetails.value = null;
  orderCustomer.value = null;
  orderDetailsLoading.value = false;
  orderDetailsError.value = "";
  drawerFulfillments.value = [];
  drawerTransactions.value = [];
  drawerTimeline.value = [];
  drawerLiveError.value = "";
  orderCapabilities.value = null;

  customerEmail.value = "";
  customerPhone.value = "";
  customersLoading.value = false;
  customersError.value = "";
  hasSearchedCustomers.value = false;
  customer.value = null;
  customerOrders.value = [];
}

function toggleDrawer() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    emit("drawer-open");
  } else {
    resetDrawerState();
    emit("drawer-close");
  }
}

function closeDrawer() {
  if (!isOpen.value) return;
  isOpen.value = false;
  resetDrawerState();
  emit("drawer-close");
}

function formatDate(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleString();
}

function formatCurrency(amount) {
  if (amount == null || amount === "") return "N/A";
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return String(amount);
  return `${num.toFixed(2)}`;
}

function formatMoney(m) {
  if (!m || m.amount == null) return "—";
  const num = parseFloat(m.amount);
  if (Number.isNaN(num)) return "—";
  return `${m.currencyCode || "USD"} ${num.toFixed(2)}`;
}

function formatAddress(addr) {
  if (!addr) return "—";
  const parts = [];
  if (addr.firstName || addr.lastName) {
    parts.push(`${addr.firstName || ""} ${addr.lastName || ""}`.trim());
  }
  if (addr.address1) parts.push(addr.address1);
  if (addr.address2) parts.push(addr.address2);
  const cityLine = [addr.city, addr.province, addr.zip]
    .filter(Boolean)
    .join(", ");
  if (cityLine) parts.push(cityLine);
  if (addr.country) parts.push(addr.country);
  return parts.filter(Boolean).join("\n");
}

/** Map order detail response into drawer state (`live` from CRM+GraphQL or /api/shopify-quick). */
function applyOrderDetailPayload(data) {
  orderDetails.value = data.order || null;
  orderCustomer.value = data.customer || null;
  orderCapabilities.value = data.capabilities || null;
  drawerFulfillments.value = [];
  drawerTransactions.value = [];
  drawerTimeline.value = [];
  drawerLiveError.value = data.liveError || "";
  if (data.live) {
    const live = data.live;
    drawerTimeline.value = Array.isArray(live.timeline)
      ? [...live.timeline]
      : [];
    drawerTransactions.value = Array.isArray(live.transactions)
      ? [...live.transactions]
      : [];
    drawerFulfillments.value = Array.isArray(live.fulfillments)
      ? [...live.fulfillments]
      : [];
  }
}

async function searchOrders() {
  if (!orderQueryTrimmed.value) return;
  ordersLoading.value = true;
  ordersError.value = "";
  hasSearchedOrders.value = true;
  ordersResults.value = [];
  orderDetails.value = null;
  orderCustomer.value = null;
  orderDetailsError.value = "";
  drawerFulfillments.value = [];
  drawerTransactions.value = [];
  drawerTimeline.value = [];
  drawerLiveError.value = "";

  try {
    const res = await axios.get("/api/shopify-quick/orders/by-number", {
      params: { orderNumber: orderQueryTrimmed.value, scopeMode: "minimal" },
      timeout: 30000,
    });
    if (!res.data?.success || !res.data.order) {
      ordersError.value = res.data?.error || "Failed to load order.";
      return;
    }
    const o = res.data.order;
    ordersResults.value = [
      {
        id: o.id,
        orderNumber: o.orderNumber,
        customerEmail: o.customerEmail,
        customerPhone: o.customerPhone,
        financialStatus: o.financialStatus,
        fulfillmentStatus: o.fulfillmentStatus,
        createdAt: o.createdAt,
      },
    ];
    applyOrderDetailPayload(res.data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Order search error:", err);
    if (err.response?.status === 404) {
      ordersError.value =
        err.response?.data?.error || "No orders found for this number.";
      return;
    }
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      ordersError.value =
        "Request timed out. The server may be slow. Please try again.";
    } else if (err.response?.status === 403) {
      ordersError.value = "You do not have permission to search orders.";
    } else if (err.response?.data?.error) {
      ordersError.value = err.response.data.error;
    } else if (err.message) {
      ordersError.value = err.message;
    } else {
      ordersError.value = "Failed to search orders.";
    }
  } finally {
    ordersLoading.value = false;
  }
}

async function loadOrderDetails(orderId) {
  if (!orderId) return;
  orderDetailsLoading.value = true;
  orderDetailsError.value = "";
  drawerFulfillments.value = [];
  drawerTransactions.value = [];
  drawerTimeline.value = [];
  drawerLiveError.value = "";
  orderCapabilities.value = null;

  try {
    const res = await axios.get(
      `/api/shopify-quick/orders/by-legacy-id/${orderId}`,
      {
        params: { scopeMode: "minimal" },
        timeout: 30000,
      }
    );
    if (!res.data?.success || !res.data.order) {
      orderDetails.value = null;
      orderCustomer.value = null;
      orderDetailsError.value =
        res.data?.error || "Failed to load order details.";
      return;
    }
    applyOrderDetailPayload(res.data);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Error loading order details:", err);
    orderDetails.value = null;
    orderCustomer.value = null;
    orderDetailsError.value =
      err.response?.data?.error ||
      err.message ||
      "Failed to load order details.";
  } finally {
    orderDetailsLoading.value = false;
  }
}

async function searchCustomers() {
  if (!hasCustomerQuery.value) return;
  customersLoading.value = true;
  customersError.value = "";
  hasSearchedCustomers.value = true;
  customer.value = null;
  customerOrders.value = [];

  try {
    const params = new URLSearchParams();
    if (customerEmail.value.trim()) {
      params.append("email", customerEmail.value.trim());
    }
    if (customerPhone.value.trim()) {
      params.append("phone", customerPhone.value.trim());
    }

    const res = await axios.get(
      `/api/shopify-quick/customers/search-orders?${params.toString()}`
    );
    if (!res.data?.success) {
      customersError.value = res.data?.error || "Failed to search customers.";
      return;
    }

    if (!res.data.customer) {
      customer.value = null;
      customerOrders.value = [];
      return;
    }

    const c = res.data.customer;
    customer.value = {
      id: c.id,
      email: c.email,
      phone: c.phone,
      firstName: c.firstName,
      lastName: c.lastName,
      fullName: c.fullName,
      totalSpent: c.totalSpent,
      ordersCount: c.ordersCount,
    };
    customerOrders.value = res.data.orders || [];
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Customer search error:", err);
    if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
      customersError.value =
        "Request timed out. The server may be slow. Please try again.";
    } else if (err.response?.status === 403) {
      customersError.value = "You do not have permission to search customers.";
    } else if (err.response?.data?.error) {
      customersError.value = err.response.data.error;
    } else if (err.message) {
      customersError.value = err.message;
    } else {
      customersError.value = "Failed to search customers.";
    }
  } finally {
    customersLoading.value = false;
  }
}

async function openOrderInDrawer(order) {
  if (!order?.id) return;
  activeTabIndex.value = 0;
  orderQuery.value = order.orderNumber || order.id.toString();
  hasSearchedOrders.value = true;
  ordersResults.value = [
    {
      id: order.id,
      orderNumber: order.orderNumber,
      customerEmail: order.customerEmail ?? null,
      customerPhone: order.customerPhone ?? null,
      financialStatus: order.financialStatus,
      fulfillmentStatus: order.fulfillmentStatus,
      createdAt: order.createdAt,
    },
  ];
  orderDetails.value = null;
  orderCustomer.value = null;
  await loadOrderDetails(order.id);
}

// Normalized line items for compact display in drawer
const orderDetailsLineItems = computed(() => {
  const o = orderDetails.value;
  if (!o) return [];
  const raw = o.rawData || o.raw_data || null;
  const src = raw?.lineItems || o.lineItems || [];
  let edges = [];
  if (Array.isArray(src?.edges)) {
    edges = src.edges.map((e) => e?.node).filter(Boolean);
  } else if (Array.isArray(src)) {
    edges = src.map((n) => n?.node || n).filter(Boolean);
  }
  return edges.map((n) => ({
    id: n.id,
    title: n.title || "Item",
    quantity: n.quantity ?? 0,
    variantTitle: n.variantTitle || n.variant?.title || null,
    sku: n.sku || n.variant?.sku || null,
  }));
});

function formatTransactionAmount(tx) {
  if (!tx) return "—";
  const amount =
    tx.amountSet?.shopMoney?.amount ??
    tx.amount?.amount ??
    tx.amount ??
    null;
  const currency =
    tx.amountSet?.shopMoney?.currencyCode ??
    tx.amount?.currencyCode ??
    "USD";
  if (amount == null) return "—";
  const num = parseFloat(amount);
  if (Number.isNaN(num)) return "—";
  return `${currency} ${num.toFixed(2)}`;
}

function statusBadgeClass(status, type) {
  if (!status) return "badge--neutral";
  const s = String(status).toLowerCase().replace(/_/g, " ");
  if (type === "financial" || type === "transaction") {
    if (["paid", "success", "captured"].some((k) => s.includes(k))) {
      return "badge--success";
    }
    if (
      ["pending", "partially", "authorized", "partially refunded"].some((k) =>
        s.includes(k)
      )
    ) {
      return "badge--warning";
    }
    if (
      ["failed", "voided", "refunded", "disputed", "error"].some((k) =>
        s.includes(k)
      )
    ) {
      return "badge--danger";
    }
  }
  if (type === "fulfillment") {
    if (["fulfilled", "delivered"].some((k) => s.includes(k))) {
      return "badge--success";
    }
    if (["in progress", "open", "partial", "scheduled"].some((k) =>
      s.includes(k)
    )) {
      return "badge--warning";
    }
    if (["cancelled", "canceled", "on hold"].some((k) => s.includes(k))) {
      return "badge--danger";
    }
  }
  return "badge--neutral";
}
</script>

<style scoped>
.right-drawer {
  display: flex;
  height: 100vh;
  width: 7px; /* handle-only when closed */
  transition: width 0.25s ease-in-out;
  position: fixed;
  top: 0;
  right: 12px;
  z-index: 1000;
}

.right-drawer--open {
  width: 380px; /* handle + panel */
}

.right-drawer__handle {
  width: 8px;
  min-width: 8px;
  max-width: 8px;
  height: 200px;
  margin: auto 0;
  border-radius: 999px 0 0 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.right-drawer__handle-bar {
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(
    to bottom,
    rgba(15, 23, 42, 0.15),
    rgba(15, 23, 42, 0.35)
  );
}

.right-drawer__panel {
  --drawer-fg: #111827;
  --drawer-fg-muted: #6b7280;
  --drawer-label: #6b7280;
  --drawer-border: #e5e7eb;
  --drawer-surface-muted: #f9fafb;
  --drawer-link: #2563eb;
  flex: 1;
  overflow: auto;
  height: 100%;
  border-left: 1px solid var(--drawer-border, #e5e7eb);
  display: flex;
  flex-direction: column;
  color: var(--drawer-fg, #111827);
}

.right-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--drawer-border, #e5e7eb);
}

.right-drawer__title {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0;
  color: var(--drawer-fg, #111827);
}

.right-drawer__close {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.1rem 0.25rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.right-drawer__close:hover {
  color: var(--drawer-fg, #111827);
}

.order-details-header {
  margin-bottom: 0.75rem;
}

.order-details-header__number {
  font-size: 1rem;
  font-weight: 600;
  color: var(--drawer-fg, #111827);
}

.order-details-header__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.35rem;
}

.right-drawer__tabs {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.right-drawer__section {
  padding: 0.75rem 1rem 1rem;
  overflow-y: auto;
  height: 100%;
}

.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--drawer-label, #4b5563);
  margin-top: 0.5rem;
  margin-bottom: 0.25rem;
}

.field-input {
  width: 100%;
}

.field-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.field-row--mt {
  margin-top: 0.75rem;
}

.field-help {
  margin-top: 0.25rem;
  font-size: 0.7rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
}

.error-text {
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #b91c1c;
}

.loading-text,
.empty-text {
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.results-list {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.results-list--compact {
  margin-top: 0.5rem;
}

.results-list__item {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  border-radius: 0.375rem;
  border: 1px solid var(--drawer-border, #e5e7eb);
  background-color: var(--drawer-surface-muted, #f9fafb);
}

.results-list__main {
  flex: 1;
  min-width: 0;
}

.results-list__order-link {
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.results-list__order-number {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--drawer-fg, #111827);
}

.results-list__meta {
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: var(--drawer-fg-muted, #6b7280);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.results-list__date {
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: var(--drawer-fg-muted, #9ca3af);
}

.results-list__cta {
  flex-shrink: 0;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 0 0.3rem;
  border-radius: 999px;
  background-color: #e5e7eb;
  color: #374151;
  font-size: 0.7rem;
  line-height: 1.3;
}

.badge--muted {
  background-color: #f3f4f6;
  color: #6b7280;
}

.badge--success {
  background-color: #dcfce7;
  color: #166534;
}

.badge--warning {
  background-color: #fef9c3;
  color: #92400e;
}

.badge--danger {
  background-color: #fee2e2;
  color: #991b1b;
}

.badge--neutral {
  background-color: #e5e7eb;
  color: #4b5563;
}

.truncate {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.customer-summary {
  margin-top: 0.75rem;
  padding: 0.5rem 0.6rem;
  border-radius: 0.375rem;
  border: 1px solid var(--drawer-border, #e5e7eb);
  background-color: var(--drawer-surface-muted, #f9fafb);
}

.customer-summary__name {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--drawer-fg, #111827);
}

.customer-summary__meta {
  margin: 0.15rem 0 0;
  font-size: 0.75rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.customer-summary__sep {
  margin: 0 0.25rem;
}

.customer-orders {
  margin-top: 0.75rem;
}

.customer-orders__title {
  margin: 0 0 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--drawer-label, #4b5563);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem 1rem;
  margin-top: 0.5rem;
}

.summary-span {
  grid-column: span 2 / span 2;
}

.summary-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--drawer-label, #6b7280);
  margin-bottom: 0.1rem;
}

.summary-value {
  font-size: 0.8rem;
  color: var(--drawer-fg, #111827);
}

.summary-value--pre {
  white-space: pre-line;
}

.items-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.items-list__item {
  padding: 0.3rem 0.35rem;
  border-radius: 0.375rem;
  border: 1px solid var(--drawer-border, #e5e7eb);
  background-color: var(--drawer-surface-muted, #f9fafb);
}

.items-list__title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--drawer-fg, #111827);
}

.items-list__meta {
  margin-top: 0.1rem;
  font-size: 0.75rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.fulfillment-card {
  border: 1px solid var(--drawer-border, #e5e7eb);
  border-radius: 0.5rem;
  padding: 0.5rem 0.6rem;
  background-color: var(--drawer-surface-muted, #f9fafb);
}

.fulfillment-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.35rem;
}

.fulfillment-card__body {
  font-size: 0.8rem;
}

.transaction-item__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.timeline-list {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.timeline-list__item {
  padding: 0.4rem 0.45rem;
  border-radius: 0.375rem;
  border: 1px solid var(--drawer-border, #e5e7eb);
  background-color: var(--drawer-surface-muted, #f9fafb);
}

.timeline-list__meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.2rem;
}

.timeline-list__date {
  font-size: 0.7rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.timeline-list__message {
  font-size: 0.78rem;
  color: var(--drawer-fg, #111827);
}

.timeline-list__author {
  margin-top: 0.15rem;
  font-size: 0.7rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.link {
  color: var(--drawer-link, #2563eb);
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.summary-muted {
  font-size: 0.72rem;
  color: var(--drawer-fg-muted, #6b7280);
}

.drawer-order-accordion {
  margin-top: 0.35rem;
}

.drawer-order-accordion .summary-grid {
  margin-top: 0.25rem;
}
</style>

